import React from 'react';
import styled, { css } from 'react-emotion';
import ImageGallery from 'react-image-gallery';
import Image from '@jetshop/ui/Image/Image';
import { constructGallery } from '@jetshop/ui/Gallery/constructGallery';
import 'react-image-gallery/styles/css/image-gallery.css';
import { withTheme } from 'emotion-theming';

const Wrapper = styled('div')`
  position: relative;
  padding-bottom: 30px;
  img {
    max-height: 100vh;
  }
  .image-gallery-image {
    text-align: center;
  }
  .image-gallery-thumbnail-inner {
    font-size: 10px;
  }
  .image-gallery-right-nav,
  .image-gallery-left-nav,
  .image-gallery-fullscreen-button {
    font-size: 2rem;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0;
    height: 2.5rem;
    width: 2.5rem;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.4);
    transition: background 0.3s ease-in-out;
    margin: 0 0.5rem;
    &:hover {
      background: rgba(255, 255, 255, 0.8);
    }
    &:before {
      color: black;
      text-shadow: none;
    }
  }
  .image-gallery-fullscreen-button {
    margin-bottom: 0.5rem;
  }
  .image-gallery-fullscreen-button:before {
    text-shadow: none;
    font-size: 0.7em;
    padding: 0;
  }
  .image-gallery-thumbnail.active {
    border-width: 1px;
  }
  .image-gallery-thumbnails-wrapper.left .image-gallery-thumbnails,
  .image-gallery-thumbnails-wrapper.right .image-gallery-thumbnails {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .image-gallery-bullets {
    bottom: -20px;
  }

  .image-gallery-bullets .image-gallery-bullet {
    background: #000;
    opacity: 0.5;
    box-shadow: none;
    padding: 3px;
    border: none;
    margin: 0 5px;

    &.active {
      background: #000;
      opacity: 1;
    }
  }
  .fullscreen {
    .image-gallery-fullscreen-button {
      margin: 2rem;
    }
    .image-gallery-right-nav,
    .image-gallery-left-nav {
      margin: 0 2rem;
    }
    img {
      width: auto;
      max-width: 100%;
    }
    .image-gallery-thumbnails-wrapper {
      display: none;
    }
  }
  &.unmounted {
    .image-gallery-thumbnails-wrapper,
    button {
      display: none;
    }
    img {
      max-width: 100%;
    }
    .image-gallery-slide {
      display: none;
    }
    .image-gallery-slide:first-of-type {
      display: block;
    }
  }
`;
const Overlay = styled('div')`
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
`;

class Gallery extends React.Component {
  constructor() {
    super(...arguments);
    this.state = {
      showBullets: true,
      isFullscreen: false,
      _mounted: false
    };
  }
  componentDidMount() {
    this.setState({ _mounted: true });
    window.addEventListener('resize', this.handleResize);
    this.handleResize();
  }
  componentWillUnmount() {
    this.setState({ _mounted: false });
    window.removeEventListener('resize', this.handleResize);
  }
  isBelowMd = () => {
    return window.matchMedia(this.props.theme.below.sm.replace('@media', ''));
  };
  handleResize = () => {
    const { matches } = this.isBelowMd();
    this.setState({ showBullets: matches });
  };
  render() {
    const galleryImages = constructGallery(
      this.props.images,
      this.props.theme,
      this.props.fullScreenSizes,
      this.props.aspect
    );

    return React.createElement(
      Wrapper,
      {
        className: `${this.state._mounted ? 'mounted' : 'unmounted'} ${css`
          .image-gallery-slide {
            background-color: ${this.props.backgroundColor};
          }
        `}`
      },
      React.createElement(ImageGallery, {
        thumbnailPosition: this.props.thumbnailPosition,
        items: galleryImages,
        autoPlay: false,
        showPlayButton: false,
        useBrowserFullscreen: false,
        showThumbnails: !this.state.showBullets,
        showBullets: this.state.showBullets,
        preventDefaultTouchmoveEvent: this.state.isFullscreen,
        onScreenChange: el => this.setState({ isFullscreen: !!el }),
        renderItem: item => {
          return React.createElement(
            'div',
            { className: 'image-gallery-image' },
            this.state.isFullscreen
              ? React.createElement(Image, {
                  src: item.original,
                  alt: item.originalAlt,
                  srcSet: item.srcSet,
                  sizes: [1, 1, 1, 1, 1600],
                  title: item.originalTitle
                })
              : React.createElement(
                  React.Fragment,
                  null,
                  React.createElement(Image, {
                    src: item.original,
                    alt: item.originalAlt,
                    sizes: this.props.sizes,
                    title: item.originalTitle || item.originalAlt,
                    aspect: this.props.aspect
                  }),
                  this.props.children &&
                    React.createElement(Overlay, null, this.props.children)
                )
          );
        },
        renderThumbInner: item => {
          return React.createElement(
            'div',
            { className: 'image-gallery-thumbnail-inner' },
            React.createElement(Image, {
              src: item.thumbnail,
              alt: item.thumbnailAlt,
              title: item.thumbnailTitle,
              sizes: [98],
              aspect: this.props.aspect
            }),
            item.thumbnailLabel &&
              React.createElement(
                'div',
                { className: 'image-gallery-thumbnail-label' },
                item.thumbnailLabel
              )
          );
        }
      })
    );
  }
}
Gallery.defaultProps = {
  sizes: [1, 1, 1 / 2, 1 / 2],
  fullScreenSizes: [1, 1, 1, 1],
  aspect: '3:4',
  thumbnailPosition: 'bottom',
  backgroundColor: 'white'
};
export default withTheme(Gallery);
